import { getCapability, getProvider } from "@aux0/connectors";
import { ArrowLeft01Icon, Link04Icon, PauseIcon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { BudgetWindows } from "@/components/accounts/budget-windows";
import { LiveAccount } from "@/components/accounts/live-account";
import { AppLink } from "@/components/common/app-link";
import { AccountStatusBadge, ProviderMark, Tag } from "@/components/common/badges";
import { CopyBlock } from "@/components/common/copy-button";
import { Icon } from "@/components/common/icon";
import { Field, Page, PageTitle, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { client } from "@/data";
import { qForProject } from "@/data/queries";
import { formatDateTime, formatNumber, relativeTime } from "@/lib/format";
import { projectPath } from "@/lib/nav";

const AUTH_LABELS: Record<string, string> = {
  oauth: "OAuth",
  qr: "QR code",
  credentials: "Credentials",
  cookie: "Session cookie",
  imap: "IMAP/SMTP",
  dav: "CalDAV/CardDAV",
  phone: "Phone + code",
  account: "Account",
};

const ATTENTION_HEADLINES: Record<string, string> = {
  checkpoint: "The provider wants the user to confirm their identity",
  qr_expired: "The device was unlinked",
  password_changed: "The password changed",
  token_expired: "The provider revoked the token",
  two_factor_required: "The provider is asking for a second factor",
  rate_limited: "The provider is refusing requests for now",
  provider_block: "The provider has blocked this account",
};

export const Route = createFileRoute("/p/$project/accounts/$accountId")({
  loader: async ({ context, params }) => {
    const q = qForProject(params.project);
    const account = await context.queryClient.ensureQueryData(
      q.account(params.accountId),
    );
    if (!account) throw notFound();
    if (client.kind === "http") return { account };
    await Promise.all([
      context.queryClient.ensureQueryData(
        q.mailThreads({ accountId: account.id, limit: 5 }),
      ),
      context.queryClient.ensureQueryData(q.calendars(account.id)),
      context.queryClient.ensureQueryData(q.contactGroups(account.id)),
      ...(account.endUserId ? [context.queryClient.ensureQueryData(q.endUsers())] : []),
    ]);
    return { account };
  },
  pendingComponent: PageSkeleton,
  component: AccountPage,
});

function AccountPage() {
  const { project, accountId } = Route.useParams();
  const { data: account } = useSuspenseQuery(qForProject(project).account(accountId));
  if (!account) throw notFound();
  if (client.kind === "http") return <LiveAccount account={account} />;
  return <FixtureAccountPage />;
}

function FixtureAccountPage() {
  const q = qForProject(Route.useParams().project);
  const { project } = Route.useParams();
  const { account } = Route.useLoaderData();
  const { data: threads } = useSuspenseQuery(
    q.mailThreads({ accountId: account.id, limit: 5 }),
  );
  const { data: calendars } = useSuspenseQuery(q.calendars(account.id));
  const { data: groups } = useSuspenseQuery(q.contactGroups(account.id));
  const { data: endUsers } = useSuspenseQuery(q.endUsers());

  const provider = getProvider(account.provider);
  const endUser = endUsers.items.find((user) => user.id === account.endUserId);

  return (
    <Page>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 w-fit text-muted-foreground"
        asChild
      >
        <AppLink to={projectPath(project, "/accounts")}>
          <Icon icon={ArrowLeft01Icon} className="size-3.5" />
          Accounts
        </AppLink>
      </Button>

      <PageTitle
        leading={<ProviderMark provider={account.provider} size="lg" />}
        title={account.handle}
        description={provider?.tagline}
        meta={
          <>
            <AccountStatusBadge status={account.status} />
            <Tag mono>{AUTH_LABELS[account.auth] ?? account.auth}</Tag>
            <span className="mono-id">{account.id}</span>
          </>
        }
        actions={
          <>
            {account.attention?.selfServe ? (
              <Button size="sm" asChild>
                <AppLink
                  to={projectPath(project, `/connect?user=${account.endUserId}`)}
                >
                  <Icon icon={Link04Icon} className="size-3.5" />
                  Send reconnect link
                </AppLink>
              </Button>
            ) : null}
            <Button variant="outline" size="sm" disabled>
              <Icon icon={PauseIcon} className="size-3.5" />
              {account.status === "paused" ? "Resume" : "Pause"}
            </Button>
          </>
        }
      />

      {/* When an account needs the user, that notice goes above everything
          else on the page: nothing below it can be acted on until it clears. */}
      {account.attention ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-lg border border-warn/30 bg-warn-soft/50 px-4 py-3.5"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span aria-hidden className="size-2 rounded-full bg-warn" />
            <h2 className="text-[13.5px] font-medium text-warn">
              {ATTENTION_HEADLINES[account.attention.code] ??
                "This account needs attention"}
            </h2>
            <span className="text-[11.5px] text-muted-foreground">
              since {formatDateTime(account.attention.since)}
            </span>
          </div>
          <p className="max-w-[72ch] text-[13px] leading-[1.55] text-ink-soft">
            {account.attention.message}
          </p>
          <p className="font-mono text-[11px] text-muted-foreground">
            account.needs_attention · code {account.attention.code} · self_serve{" "}
            {String(account.attention.selfServe)}
          </p>
        </div>
      ) : null}

      {account.backfill ? (
        <Panel title="History backfill" bodyClassName="flex flex-col gap-3 p-4">
          <Progress value={account.backfill.progress * 100} className="h-1.5" />
          <div className="flex flex-wrap justify-between gap-3 text-[12.5px] text-muted-foreground">
            <span>
              {formatNumber(account.backfill.messagesImported)} messages so far
            </span>
            <span>
              Reached {formatDateTime(account.backfill.reachedAt)} ·{" "}
              {Math.round(account.backfill.progress * 100)}%
            </span>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Reads work during a backfill. Sends are accepted and queued until it
            finishes, so nothing your product does has to wait for it.
          </p>
        </Panel>
      ) : null}

      {/* An account's four numbers depend on what it holds. "Threads: 0" on a
          calendar reads as "nothing synced" rather than "not a thing this
          account has", which is the sort of number that generates a support
          ticket. */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        {calendars.length > 0 ? (
          <>
            <Stat label="Calendars" value={calendars.length} />
            <Stat
              label="Writable"
              value={
                calendars.filter(
                  (calendar) =>
                    calendar.access !== "reader" && calendar.access !== "free_busy",
                ).length
              }
              footnote="The rest are read or free/busy only"
            />
            <Stat
              label="Events written"
              value={formatNumber(account.stats?.sent)}
              footnote="Through the API, this period"
            />
            <Stat label="Events read" value={formatNumber(account.stats?.received)} />
          </>
        ) : groups.length > 0 ? (
          <>
            <Stat label="Address books" value={groups.length} />
            <Stat
              label="Contacts"
              value={formatNumber(account.stats?.received)}
              footnote="As the provider reports them"
            />
            <Stat
              label="Writes"
              value={formatNumber(account.stats?.sent)}
              footnote="Through the API, this period"
            />
            <Stat
              label="Derived addresses"
              value={formatNumber(account.stats?.unread)}
              footnote="Seen in mail, never written back"
            />
          </>
        ) : (
          <>
            <Stat label="Threads" value={formatNumber(account.stats?.chats)} />
            <Stat
              label="Unread"
              value={formatNumber(account.stats?.unread)}
              footnote="On the provider side"
            />
            <Stat label="Sent this period" value={formatNumber(account.stats?.sent)} />
            <Stat
              label="Received this period"
              value={formatNumber(account.stats?.received)}
            />
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Connection" bodyClassName="px-4 py-2">
          <dl>
            <Field label="End user">
              {endUser ? (
                <span>
                  {endUser.name}
                  <span className="mono-id ml-2">{endUser.id}</span>
                </span>
              ) : (
                account.endUserId
              )}
            </Field>
            <Field label="Provider id">
              <span className="font-mono text-[12px] break-all">
                {account.providerId}
              </span>
            </Field>
            <Field label="Linked by">{AUTH_LABELS[account.auth] ?? account.auth}</Field>
            <Field label="Connected">{formatDateTime(account.connectedAt)}</Field>
            <Field label="Last sync">
              {relativeTime(account.lastSyncAt)}
              <span className="ml-2 text-muted-foreground">
                {formatDateTime(account.lastSyncAt)}
              </span>
            </Field>
          </dl>
        </Panel>

        <Panel
          title="Provider limits"
          description="What this account has spent against the provider's own windows"
          bodyClassName="p-4"
        >
          {account.budget.windows.length === 0 ? (
            <EmptyState
              title="No quota to report"
              description="Aux0 stopped reaching this provider as this account, so there is no window to meter. Reconnecting starts the counters again."
              className="py-6"
            />
          ) : (
            <BudgetWindows budget={account.budget} />
          )}
        </Panel>
      </div>

      {calendars.length > 0 ? (
        <Panel
          title="Calendars"
          description="What this account can read, and what it is allowed to write"
          bodyClassName="divide-y"
        >
          {calendars.map((calendar) => (
            <div
              key={calendar.id}
              className="flex flex-wrap items-center gap-3 px-4 py-2.5"
            >
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-[3px]"
                style={{ backgroundColor: calendar.color }}
              />
              <span className="min-w-0 flex-1 truncate text-[13px]">
                {calendar.name}
              </span>
              {calendar.primary ? <Tag mono>primary</Tag> : null}
              <Tag
                mono
                title={
                  calendar.access === "free_busy"
                    ? "Aux0 can see that this calendar is busy, and nothing else"
                    : undefined
                }
                className={
                  calendar.access === "free_busy"
                    ? "border-border text-ink-faint"
                    : "border-border text-muted-foreground"
                }
              >
                {calendar.access.replace("_", " ")}
              </Tag>
              <span className="hidden shrink-0 text-[11px] text-ink-faint sm:block">
                {calendar.timeZone}
              </span>
            </div>
          ))}
        </Panel>
      ) : null}

      {groups.length > 0 ? (
        <Panel
          title="Address books"
          description="The groups this account exposes, and how many each holds"
          bodyClassName="divide-y"
        >
          {groups.map((group) => (
            <div key={group.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1 truncate text-[13px]">{group.name}</span>
              {group.system ? <Tag mono>provider</Tag> : null}
              <span className="shrink-0 text-[12px] text-muted-foreground tabular">
                {formatNumber(group.count)}
              </span>
            </div>
          ))}
        </Panel>
      ) : null}

      {threads.items.length > 0 ? (
        <Panel
          title="Recent threads"
          actions={
            <Button variant="ghost" size="sm" asChild>
              <AppLink to={projectPath(project, "/mail")}>Open mail</AppLink>
            </Button>
          }
          bodyClassName="divide-y"
        >
          {threads.items.map((thread) => (
            <AppLink
              key={thread.id}
              to={projectPath(project, `/mail/${thread.id}`)}
              className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">
                  {thread.subject}
                </span>
                <span className="block truncate text-[11.5px] text-muted-foreground">
                  {thread.snippet}
                </span>
              </span>
              {thread.unread > 0 ? (
                <Tag className="border-brand/30 bg-brand-soft/60 text-brand">
                  {thread.unread}
                </Tag>
              ) : null}
              <span className="shrink-0 text-[11px] text-ink-faint">
                {relativeTime(thread.lastMessageAt)}
              </span>
            </AppLink>
          ))}
        </Panel>
      ) : null}

      <Panel
        title="Capabilities"
        description={`What ${provider?.name ?? account.provider} exposes through the unified API`}
        bodyClassName="flex flex-wrap gap-1.5 p-4"
      >
        {(provider?.capabilities ?? []).map((id) => (
          <Tag key={id} title={getCapability(id).description}>
            {getCapability(id).label}
          </Tag>
        ))}
      </Panel>

      <Panel title="Fetch this account" bodyClassName="p-4">
        <CopyBlock
          value={`curl https://api.aux0.com/v1/accounts/${account.id} \\\n  -H "Authorization: Bearer $AUX0_API_KEY"`}
        />
      </Panel>
    </Page>
  );
}
